import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  Button,
  CircularProgress,
  alpha,
  useTheme,
  Divider
} from '@mui/material';
import {
  Message as MessageIcon,
  Person as PersonIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ComplaintsService, { ComplaintThread } from 'src/services/complaints-service';

const ComplaintsDashboardWidget: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const complaintsService = ComplaintsService.getInstance();
  const [loading, setLoading] = useState(true);
  const [unreadThreads, setUnreadThreads] = useState<ComplaintThread[]>([]);
  const [totalUnread, setTotalUnread] = useState(0);

  const loadUnreadComplaints = useCallback(async () => {
    try {
      setLoading(true);
      const threads = await complaintsService.getAllThreads();
      const unread = threads
        .filter(thread => thread.unreadByAdminCount > 0)
        .sort((a, b) => new Date(b.lastActivityAt).getTime() - new Date(a.lastActivityAt).getTime())
        .slice(0, 5); // Show top 5 most recent

      const total = threads.reduce((sum, thread) => sum + thread.unreadByAdminCount, 0);

      setUnreadThreads(unread);
      setTotalUnread(total);
    } catch (error) {
      console.error('Failed to load complaints:', error);
    } finally {
      setLoading(false);
    }
  }, [complaintsService]);

  useEffect(() => {
    loadUnreadComplaints();
    // Poll for updates every 30 seconds
    const interval = setInterval(loadUnreadComplaints, 30000);
    return () => clearInterval(interval);
  }, [loadUnreadComplaints]);

  const handleThreadClick = (threadId: string) => {
    navigate(`/complaints/${threadId}`);
  };

  const handleViewAll = () => {
    navigate('/complaints');
  };

  const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.shadows[1],
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[3]
        }
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                background: `linear-gradient(135deg, ${alpha('#4CAF50', 0.2)} 0%, ${alpha('#00897B', 0.15)} 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#4CAF50'
              }}
            >
              <MessageIcon fontSize="medium" />
            </Box>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  background: `linear-gradient(135deg, #4CAF50 0%, #00897B 100%)`,
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                Tenant Messages
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Recent complaints & inquiries
              </Typography>
            </Box>
          </Box>
          {totalUnread > 0 && (
            <Chip
              label={`${totalUnread} unread`}
              size="small"
              color="error"
              sx={{
                fontWeight: 600,
                animation: 'pulse 2s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%, 100%': { transform: 'scale(1)' },
                  '50%': { transform: 'scale(1.05)' }
                }
              }}
            />
          )}
        </Box>

        <Divider sx={{ mb: 2 }} />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : unreadThreads.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 4,
              gap: 1
            }}
          >
            <MessageIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
            <Typography variant="body2" color="text.secondary">
              No unread messages
            </Typography>
          </Box>
        ) : (
          <>
            <List disablePadding>
              {unreadThreads.map((thread, index) => (
                <React.Fragment key={thread.id}>
                  <ListItem
                    sx={{
                      px: 0,
                      py: 1.5,
                      cursor: 'pointer',
                      borderRadius: 1.5,
                      transition: 'background 0.2s',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.04)
                      }
                    }}
                    onClick={() => handleThreadClick(thread.id)}
                  >
                    <ListItemAvatar>
                      <Avatar
                        sx={{
                          bgcolor: alpha('#4CAF50', 0.15),
                          color: '#4CAF50'
                        }}
                      >
                        <PersonIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="subtitle2" fontWeight={600} noWrap>
                            {thread.tenantName}
                          </Typography>
                          <Chip
                            label={complaintsService.getStatusLabel(thread.status)}
                            size="small"
                            color={complaintsService.getStatusColor(thread.status)}
                            sx={{ height: 18, fontSize: '0.65rem' }}
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography
                            variant="body2"
                            color="text.primary"
                            noWrap
                            sx={{ mb: 0.5 }}
                          >
                            {thread.title}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              label={`${thread.unreadByAdminCount} new`}
                              size="small"
                              color="error"
                              sx={{ height: 16, fontSize: '0.6rem' }}
                            />
                            <Typography variant="caption" color="text.disabled">
                              {formatTimeAgo(thread.lastActivityAt)}
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < unreadThreads.length - 1 && <Divider component="li" />}
                </React.Fragment>
              ))}
            </List>

            <Button
              fullWidth
              variant="outlined"
              endIcon={<ArrowForwardIcon />}
              onClick={handleViewAll}
              sx={{
                mt: 2,
                borderColor: alpha('#4CAF50', 0.3),
                color: '#4CAF50',
                '&:hover': {
                  borderColor: '#4CAF50',
                  backgroundColor: alpha('#4CAF50', 0.08)
                }
              }}
            >
              View All Complaints
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ComplaintsDashboardWidget;
